import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import { withStyles, FormHelperText } from '@material-ui/core';
import Select, { components } from 'react-select';
import { ArrowDropDown } from '@material-ui/icons';

class FHEReactSelect extends PureComponent {
    static propTypes = {
        disabled: PropTypes.bool,
        classes: PropTypes.object.isRequired,
        error: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        theme: PropTypes.object.isRequired,
        multiple: PropTypes.bool,
        searchable: PropTypes.bool,
        label: PropTypes.string,
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        placeholder: PropTypes.string,
        onChange: PropTypes.func.isRequired,
        choices: PropTypes.array.isRequired,
        onClickLabel: PropTypes.func,
        suffix: PropTypes.string
    };

    static defaultProps = {
        suffix: null,
        onClickLabel: null,
        disabled: false,
        multiple: false,
        searchable: false,
        error: null,
        label: '',
        name: '',
        placeholder: '',
        value: []
    };

    render() {
        const { name, disabled, onChange, multiple, choices, searchable, placeholder, value, theme, ...restProps } = this.props;
        return (
            <Select
                {...restProps}
                name={name}
                noOptionsMessage={() => Translator.translate('general.noOption')}
                placeholder={placeholder}
                options={choices.sort(compare)}
                isDisabled={disabled}
                isMulti={multiple}
                isSearchable={searchable}
                onChange={onChange}
                value={value}
                components={{
                    DropdownIndicator,
                    Control,
                    MultiValueLabel,
                    SingleValue,
                    IndicatorsContainer,
                    MultiValueContainer
                }}
                styles={{
                    input: styles => {
                        return {
                            ...styles,
                            margin: 0
                        };
                    },
                    singleValue: styles => ({
                        ...styles,
                        color: theme.palette.common.black
                    }),
                    valueContainer: styles => ({
                        ...styles,
                        paddingLeft: 0
                    }),
                    container: styles => ({
                        ...styles,
                        width: '100%'
                    }),
                    indicatorSeparator: () => {},
                    indicatorsContainer: styles => ({
                        ...styles,
                        height: 32
                    }),
                    dropdownIndicator: styles => ({
                        ...styles,
                        padding: 0,
                        paddingBottom: 10,
                        color: theme.palette.colors.grey700
                    }),
                    control: (styles, { isDisabled, isFocused }) => ({
                        ...styles,
                        minHeight: 28,
                        padding: 0,
                        backgroundColor: 0,
                        border: isFocused ? 0 : 0,
                        borderColor: 0,
                        boxShadow: isFocused ? 0 : 0,
                        borderRadius: 0,
                        borderBottom: isDisabled
                            ? `1px dotted ${theme.palette.colors.grey600}`
                            : isFocused
                            ? `2px solid ${theme.palette.common.black}`
                            : `1px solid ${theme.palette.colors.grey600}`,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        '&:hover': {
                            border: isFocused ? 0 : 0,
                            borderBottom: isDisabled ? `1px dotted ${theme.palette.colors.grey600}` : `2px solid ${theme.palette.common.black}`
                        }
                    }),
                    menu: styles => {
                        return {
                            ...styles,
                            zIndex: 1000,
                            marginTop: '-20px',
                            backgroundColor: theme.palette.common.white
                        };
                    },
                    option: (styles, { isDisabled, isSelected }) => {
                        return {
                            ...styles,
                            color: isSelected && theme.palette.primary.main,
                            fontWeight: isSelected && 'bold',
                            backgroundColor: theme.palette.common.white,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            '&:hover': {
                                backgroundColor: theme.palette.colors.grey200
                            }
                        };
                    },
                    multiValue: styles => {
                        return {
                            ...styles,
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: 30,
                            padding: '1px 2px'
                        };
                    },
                    multiValueLabel: (styles, { isDisabled, selectProps }) => ({
                        ...styles,
                        cursor: isDisabled ? 'pointer' : styles.cursor,
                        pointerEvents: isDisabled && selectProps.onClickLabel ? 'auto' : styles.pointerEvents,
                        color: theme.palette.common.white,
                        padding: 0
                    }),
                    multiValueRemove: styles => ({
                        ...styles,
                        color: theme.palette.common.white,
                        ':hover': {
                            color: theme.palette.error.main
                        }
                    })
                }}
            />
        );
    }
}

const MenuList = props => {
    const { options, children, ...restProps } = props;
    const newChildren = children.length > 50 ? children.slice(0, 50) : children;
    const newOptions = options.length > 50 ? options.slice(0, 50) : options;
    return (
        <components.MenuList {...restProps} options={newOptions}>
            {newChildren}
        </components.MenuList>
    );
};

const compare = ( a, b ) => {
    if ( a.label < b.label ){
      return -1;
    }
    if ( a.label > b.label ){
      return 1;
    }
    return 0;
  }

const IndicatorsContainer = props => {
    const { selectProps } = props;
    if (selectProps.suffix) {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: 5 }}>{selectProps.suffix}</div>
                <components.IndicatorsContainer {...props} />
            </div>
        );
    } else {
        return <components.IndicatorsContainer {...props} />;
    }
};

const SingleValue = props => {
    const { selectProps, data } = props;

    if (selectProps.customRender) {
        return selectProps.customRender(data);
    } else {
        return <components.SingleValue {...props} />;
    }
};

const MultiValueLabel = props => {
    const { selectProps, data, innerProps } = props;
    const customInnerProps = { ...innerProps };

    if (selectProps.isDisabled && selectProps.onClickLabel) {
        customInnerProps.onClick = () => selectProps.onClickLabel(data.value);
    }
    return <components.MultiValueLabel {...props} innerProps={customInnerProps} />;
};

const MultiValueContainer = props => {
    const { selectProps, data } = props;

    if (selectProps.customRender) {
        return selectProps.customRender(data);
    } else {
        return <components.MultiValueContainer {...props} />;
    }
};

const DropdownIndicator = props => {
    return (
        <components.DropdownIndicator {...props}>
            <ArrowDropDown />
        </components.DropdownIndicator>
    );
};

const Control = props => {
    const { selectProps } = props;
    return (
        <Fragment>
            <div className={`${selectProps.classes.label} ${selectProps.error ? selectProps.classes.FormErrorText : ''}`}>{selectProps.label}</div>
            <components.Control {...props} />
            <FormHelperText className={selectProps.classes.FormErrorText} component="div">
                {selectProps.error}
            </FormHelperText>
        </Fragment>
    );
};

MultiValueLabel.propTypes = {
    selectProps: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired,
    innerProps: PropTypes.object.isRequired
};

MenuList.propTypes = {
    children: PropTypes.array.isRequired,
    options: PropTypes.array.isRequired
};
Control.propTypes = {
    selectProps: PropTypes.object.isRequired
};
const styles = theme => ({
    FHESelectFieldContainer: {
        top: 11,
        width: '100%',
        minHeight: 72
    },
    label: {
        color: '#858585',
        fontSize: '0.9em',
        marginTop: 5
    },
    FormErrorText: {
        color: theme.palette.error.main
    }
});

export default withStyles(styles, { withTheme: true })(FHEReactSelect);



// WEBPACK FOOTER //
// ./data/components/FHEReactSelect.js